import {
  Droplets,
  Compass,
  Ruler,
  HardHat,
  ClipboardCheck,
  Trees,
  ArrowRight,
} from "lucide-react";
import {
  Nav,
  Footer,
  PageHero,
  CtaBlock,
  TrustStrip,
  usePageSeo,
  RESERVOIR_COMPLETE_URL,
  RESERVOIR_SURVEY_URL,
  RESERVOIR_CONSTRUCTION_URL,
} from "../layout/shared";

const STAGES = [
  {
    n: "1",
    t: "Feasibility",
    d: "A first review of site, water source, water demand, capacity, drainage, planning context and material balance.",
    icon: Compass,
    img: RESERVOIR_SURVEY_URL,
  },
  {
    n: "2",
    t: "Planning coordination",
    d: "Planning-screening checklist including permitted-development scope, EIA screening and the separate Natural England agricultural EIA question.",
    icon: ClipboardCheck,
    img: "/images/reservoir-planning.webp",
  },
  {
    n: "3",
    t: "Water resource",
    d: "Pre-application on abstraction, impounding and, where relevant, hydrogeological impact through the applicable specialists.",
    icon: Droplets,
    img: "/images/reservoir-water-resource.webp",
  },
  {
    n: "4",
    t: "Engineering",
    d: "Design and engineering appointments through qualified reservoir-panel, civil, geotechnical and drainage professionals.",
    icon: Ruler,
    img: "/images/reservoir-engineering.webp",
  },
  {
    n: "5",
    t: "Construction",
    d: "Earthworks, lining, inlet and outlet works and site management, with the applicable CDM appointments in place.",
    icon: HardHat,
    img: RESERVOIR_CONSTRUCTION_URL,
  },
  {
    n: "6",
    t: "Aftercare",
    d: "Inspection, monitoring and record-keeping requirements agreed at handover.",
    icon: Trees,
    img: "/images/reservoir-aftercare.webp",
  },
];

const BEFORE_A_VISIT = [
  "The farm address, postcode and access route.",
  "A rough intended reservoir capacity, purpose and target completion date.",
  "Any existing site plans, drainage, water-source or ground information you already hold.",
  "Any prior planning correspondence, feasibility work or engineering advice already commissioned.",
  "Any concerns about drainage, flood risk, water source or neighbours that we should know about.",
];

const REGULATORY = [
  "Planning permission and, where applicable, permitted-development screening.",
  "Environmental impact assessment screening, including the separate agricultural EIA question handled by Natural England.",
  "Environment Agency abstraction and impounding pre-application, where relevant.",
  "Flood-risk assessment and flood-risk activity permits where the location or design triggers them.",
  "Reservoir owner and operator duties for large raised reservoirs, if applicable.",
  "CDM 2015 duties, pre-construction information and construction-phase plan for the works.",
];

const FarmReservoirs = () => {
  usePageSeo({
    title: "Farm Reservoir Construction · 3 Counties Recycling",
    description:
      "Farm reservoir feasibility, planning coordination, engineering support and construction across Worcestershire, Herefordshire and Gloucestershire.",
    path: "/farm-reservoirs",
  });

  return (
    <main data-testid="reservoirs-root" className="bg-white text-[#1a1a1a]">
      <Nav />
      <PageHero
        kicker="Rural infrastructure · water storage · coordinated route"
        title="One coordinated route from feasibility through construction to"
        accent="aftercare."
        body="3 Counties Recycling coordinates the qualified specialists needed to take a farm reservoir project from an initial feasibility review through planning, engineering, material balance, earthworks, construction and aftercare. We do not provide regulated engineering design ourselves."
        image={RESERVOIR_COMPLETE_URL}
      />

      <section className="bg-white py-16 border-b border-[#024120]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <TrustStrip variant="light" />
        </div>
      </section>

      {/* Stages */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-14 max-w-3xl">
            <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#46B649]">
              / Six stages we coordinate
            </p>
            <h2 className="mt-3 font-display font-black text-3xl md:text-4xl lg:text-[44px] tracking-tight text-[#024120] leading-[1.1]">
              One point of contact, the right specialist at each stage.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {STAGES.map((s) => (
              <div
                key={s.n}
                data-testid={`reservoir-stage-${s.n}`}
                className="bg-[#F2F2F2] flex flex-col overflow-hidden"
              >
                {s.img && (
                  <div className="relative aspect-[4/3] bg-[#F2F2F2]">
                    <img
                      src={s.img}
                      alt={s.t}
                      data-testid={`reservoir-stage-image-${s.n}`}
                      className="absolute inset-0 w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="p-7 flex flex-col flex-1">
                  <div className="flex items-center gap-4">
                    <span className="inline-flex h-12 w-12 items-center justify-center bg-[#024120] text-[#46B649]">
                      <s.icon className="w-6 h-6" strokeWidth={1.6} />
                    </span>
                    <span className="font-display font-black text-3xl text-[#FF6600] leading-none">
                      {s.n}
                    </span>
                  </div>
                  <p className="mt-5 font-display font-bold text-xl text-[#024120]">
                    {s.t}
                  </p>
                  <p className="mt-2 text-[15px] text-[#4A4A4A] leading-relaxed">
                    {s.d}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before feasibility */}
      <section className="bg-[#024120] text-white py-24 md:py-32 grain overflow-hidden relative">
        <div className="max-w-4xl mx-auto px-6 md:px-10 relative">
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#FF6600]">
            / Before a feasibility visit
          </p>
          <h2 className="mt-3 font-display font-black text-3xl md:text-4xl lg:text-[44px] tracking-tight leading-[1.1]">
            What to gather when you first get in touch.
          </h2>
          <p className="mt-6 text-white/80 text-base md:text-lg leading-relaxed">
            The more accurately we can describe the site and the intent from
            the start, the more useful the feasibility visit and quotation
            will be.
          </p>
          <ul className="mt-10 space-y-3">
            {BEFORE_A_VISIT.map((b, i) => (
              <li
                key={b}
                className="flex items-start gap-4 bg-white/5 border-l-4 border-[#46B649] px-5 py-4"
              >
                <span className="font-display font-black text-2xl text-[#46B649] leading-none tabular-nums">
                  0{i + 1}
                </span>
                <span className="text-white/85 text-[15px] leading-relaxed">
                  {b}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Regulatory */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#46B649]">
            / Regulatory context
          </p>
          <h2 className="mt-3 font-display font-black text-3xl md:text-4xl lg:text-[44px] tracking-tight text-[#024120] leading-[1.1]">
            The approvals a reservoir project can touch.
          </h2>
          <p className="mt-6 text-[#4A4A4A] text-base md:text-lg leading-relaxed">
            Not every project needs every approval. The applicable route
            depends on the size, source, use and location. We flag the
            likely regulatory picture at feasibility so the project owner
            knows what will be needed and who will be responsible.
          </p>
          <ul className="mt-10 space-y-3">
            {REGULATORY.map((r) => (
              <li
                key={r}
                className="flex items-start gap-3 bg-[#F2F2F2] px-5 py-4 text-[15px] text-[#024120] leading-relaxed"
              >
                <ArrowRight className="w-4 h-4 text-[#46B649] shrink-0 mt-1" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
          <p className="mt-8 text-sm text-[#4A4A4A] leading-relaxed italic">
            Any use of waste material in a reservoir project is only pursued
            through a currently applicable authorised route. Where that route
            is time-limited or under review, the position is rechecked for
            every relevant project.
          </p>
        </div>
      </section>

      <CtaBlock
        title="Thinking about a farm reservoir? Start with a feasibility conversation."
        body="Send the farm postcode, intended purpose, rough capacity, target completion date and any existing information you have. We will describe the next step, the likely specialists and the information still needed."
        primaryLabel="Arrange a feasibility conversation"
      />

      <Footer />
    </main>
  );
};

export default FarmReservoirs;
